import { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from '../hooks/useIntractionTranslate'
import { useAppDispatch, useAppSelector } from '../../store'
import { selectUnviewedGameReminders } from '../../store/selectors/inAppNotificationsSelectors'
import { markActivityNotified } from '../../store/slices/activitiesSlice'
import { markViewedLocally } from '../../store/slices/inAppNotificationsSlice'
import { processHandlers } from '../../apiLayer/processHandlers'
import { ActivityWithSession } from '../../types/GeneralTypes'
import { OrgRole } from 'pure-js/types/OrganizationMembership'
import { ActivityType } from 'pure-js/enums/ActivityType'
import { isAIDebriefActivity, isGameActivityType } from 'pure-js/libs/isAIDebriefActivity'
import { GameReminderPayload } from 'pure-js/types/InAppNotification'

// Simple unique ID generator
let notificationIdCounter = 0
const generateNotificationId = () => `activity-notification-${Date.now()}-${++notificationIdCounter}`

export interface ActivityReminderNotification {
  id: string
  activity: ActivityWithSession
  /** Which lead time fired: the number of minutes to the scheduled start the reminder speaks of. */
  minutesBeforeStart: number
  orgRole?: OrgRole
  timestamp: number
  inAppNotificationId?: string
}

interface UseActivityNotificationsProps {
  notifiedActivityIds: string[]
  orgRole?: OrgRole
  testNotificationTrigger?: number
}

const ONE_MINUTE_MS = 60 * 1000
const CHECK_INTERVAL_MS = 30 * 1000 // 30 s
/** Lead times a reminder fires at, once each per activity. One entry: a single nudge is enough, more is nagging. */
export const REMINDER_LEAD_TIMES_MINUTES = [5] as const
/** Which reminders were already shown are remembered per activity AND lead time. */
export const buildReminderKey = (activityId: string, minutesBeforeStart: number): string =>
  `${activityId}@${minutesBeforeStart}`

/** Activity types that trigger a start reminder: game and AI debrief only (exclude Introduction and Facilitated reflection). */
const NOTIFIABLE_ACTIVITY_TYPES = new Set<ActivityType>([
  ActivityType.FIRST_GAME,
  ActivityType.SHORT_GAME,
  ActivityType.LONG_GAME,
  ActivityType.SHORT_REFLECTION, // AI coached team debrief
  ActivityType.LONG_REFLECTION // group reflection
])

export const shouldNotifyForActivityType = (type: string | undefined): boolean =>
  Boolean(type && NOTIFIABLE_ACTIVITY_TYPES.has(type as ActivityType))

type TranslateFunction = ReturnType<typeof useTranslation>['t']

// A plain name that reads inside "… starts in N min": what the activity is, never the sprint it belongs to.
const getReminderTitle = (activityType: ActivityType, t: TranslateFunction): string => {
  if (isAIDebriefActivity(activityType)) return t('gamer.notifications.aiDebrief', 'AI debrief')
  if (activityType === ActivityType.LONG_REFLECTION) return t('gamer.notifications.groupReflection', 'Group reflection')
  if (isGameActivityType(activityType)) return t('gamer.notifications.game', 'Game')
  return ''
}

/** The name a reminder calls its activity by, wherever it is shown. */
export const getReminderActivityName = (activity: ActivityWithSession, t: TranslateFunction): string =>
  activity.title || t('gamer.notifications.activity', 'Activity')

// What the reminder says is left: the real remaining time, capped at the lead time it fired for (the 30 s check
// tolerance would otherwise round a 5-minute reminder up to 6) and never below one minute.
const toMinutesLeft = (timeUntilStartMs: number, leadMinutes: number): number =>
  Math.min(leadMinutes, Math.max(1, Math.ceil(timeUntilStartMs / ONE_MINUTE_MS)))

// The smallest lead time the start is now inside of — opening the app late fires ONE reminder, not every missed one.
const findDueLeadTime = (timeUntilStartMs: number): number | undefined => {
  if (timeUntilStartMs <= 0) return undefined
  const ascendingLeadTimes = [...REMINDER_LEAD_TIMES_MINUTES].sort((first, second) => first - second)
  return ascendingLeadTimes.find((leadMinutes) => timeUntilStartMs <= leadMinutes * ONE_MINUTE_MS + CHECK_INTERVAL_MS)
}

/**
 * Hook for managing activity reminders.
 * Reads GAME_REMINDER notifications from the inAppNotificationsSlice (backend-driven)
 * and raises a reminder at each lead time before the activity starts.
 */
export const useActivityNotifications = ({
  notifiedActivityIds,
  orgRole,
  testNotificationTrigger
}: UseActivityNotificationsProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [notifications, setNotifications] = useState<ActivityReminderNotification[]>([])
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const notifiedRef = useRef<Set<string>>(new Set(notifiedActivityIds))
  const lastTestTriggerRef = useRef<number>(0)

  const gameReminderNotifications = useAppSelector(selectUnviewedGameReminders)

  // Sync notified IDs with ref
  useEffect(() => {
    notifiedRef.current = new Set(notifiedActivityIds)
  }, [notifiedActivityIds])

  // Check for GAME_REMINDER notifications whose start is inside a lead time not yet reminded about
  const checkActivities = useCallback(() => {
    const now = Date.now()
    const newNotifications: ActivityReminderNotification[] = []

    gameReminderNotifications.forEach((inAppNotification) => {
      const payload = inAppNotification.payload as GameReminderPayload
      const activityId = payload.activityId

      const timeUntilStartMs = new Date(payload.scheduledStartTime).getTime() - now
      const dueLeadMinutes = findDueLeadTime(timeUntilStartMs)
      if (dueLeadMinutes === undefined || notifiedRef.current.has(buildReminderKey(activityId, dueLeadMinutes))) {
        return
      }

      // Every longer lead time is spent too: a reminder that arrives late never fires the earlier one after it.
      REMINDER_LEAD_TIMES_MINUTES.filter((leadMinutes) => leadMinutes >= dueLeadMinutes).forEach((leadMinutes) => {
        const reminderKey = buildReminderKey(activityId, leadMinutes)
        dispatch(markActivityNotified(reminderKey))
        notifiedRef.current.add(reminderKey)
      })

      // Construct an ActivityWithSession from the GameReminderPayload
      const activity: ActivityWithSession = {
        id: activityId,
        title: getReminderTitle(payload.activityType, t),
        description: '',
        startDate: payload.scheduledStartTime,
        endDate: payload.scheduledStartTime,
        endedAt: '',
        completedActivityTeamIds: [],
        emailsSent: [],
        type: payload.activityType,
        sprintTitle: payload.sprintTitle,
        sprintId: payload.sprintId,
        teamIds: [],
        participants: [],
        session: undefined
      }

      newNotifications.push({
        id: generateNotificationId(),
        activity,
        minutesBeforeStart: toMinutesLeft(timeUntilStartMs, dueLeadMinutes),
        orgRole,
        timestamp: now,
        inAppNotificationId: inAppNotification.id
      })
    })

    if (newNotifications.length > 0) {
      setNotifications((prev) => [...prev, ...newNotifications])
    }
  }, [gameReminderNotifications, dispatch, orgRole, t])

  // Set up interval to check activities
  useEffect(() => {
    // Initial check
    checkActivities()

    // Set up periodic checking
    intervalRef.current = setInterval(() => {
      checkActivities()
    }, CHECK_INTERVAL_MS)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [checkActivities])

  // Dismiss notification (no API call — does not mark as viewed in Firestore)
  const dismissNotification = useCallback((notificationId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId))
  }, [])

  // View notification — marks the associated inAppNotification as viewed in Firestore
  const viewNotification = useCallback(
    (notificationId: string) => {
      const item = notifications.find((n) => n.id === notificationId)
      if (item?.inAppNotificationId) {
        dispatch(markViewedLocally(item.inAppNotificationId))
        processHandlers.markNotificationViewed(item.inAppNotificationId).catch(() => {})
      }
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId))
    },
    [dispatch, notifications]
  )

  // Handle test notification trigger
  useEffect(() => {
    if (testNotificationTrigger && testNotificationTrigger !== lastTestTriggerRef.current) {
      lastTestTriggerRef.current = testNotificationTrigger
      const [longestLeadMinutes] = REMINDER_LEAD_TIMES_MINUTES

      // Create test activity with all required fields
      const testActivity: ActivityWithSession = {
        id: 'test-notification-activity',
        title: 'Test Activity',
        description: 'This is a test notification activity',
        startDate: new Date(Date.now() + longestLeadMinutes * ONE_MINUTE_MS).toISOString(),
        endDate: new Date(Date.now() + (longestLeadMinutes + 1) * ONE_MINUTE_MS).toISOString(),
        endedAt: '',
        completedActivityTeamIds: [],
        emailsSent: [],
        type: ActivityType.SHORT_GAME,
        sprintTitle: 'Test Sprint',
        sprintId: 'test-sprint',
        teamIds: [],
        participants: [],
        session: undefined
      }

      const testNotification: ActivityReminderNotification = {
        id: generateNotificationId(),
        activity: testActivity,
        minutesBeforeStart: longestLeadMinutes,
        orgRole,
        timestamp: Date.now()
      }

      setNotifications((prev) => [...prev, testNotification])
    }
  }, [testNotificationTrigger, orgRole])

  // Clean up old notifications (older than 5 minutes)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const fiveMinutesAgo = Date.now() - 5 * ONE_MINUTE_MS
      setNotifications((prev) => prev.filter((n) => n.timestamp > fiveMinutesAgo))
    }, ONE_MINUTE_MS) // Check every minute

    return () => clearInterval(cleanupInterval)
  }, [])

  return {
    notifications,
    dismissNotification,
    viewNotification
  }
}
